// src/utils/services/AdvancedMD/ehrNotesByTemplate.js
//
// Lectura GENÉRICA de notas clínicas EHR por templateKey — cubre los 7
// templates activos (serie 100001781/1000018xx de este office key).
//
// Documentación fuente:
//   docs/NotasClinicas/Endpoints/README-ConsumoEndpoints.md
//   docs/NotasClinicas/Endpoints/README-NotasClinicas-7Templates.md
//
// A diferencia de los servicios legacy (telehealth20.js, annualWellness20.js,
// followUpExam20.js, hospitalDischargeFollowUp.js — que apuntan a los
// templateid VIEJOS 100001773/74/75), este endpoint resuelve el template por
// una templateKey del catálogo del backend y lee los templates NUEVOS.

import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const EHR_NOTES_BASE_URL = `${API_BASE_URL}/api/advancedmd/ehrnotes`;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

/**
 * Catálogo de los 7 templates activos para lectura (orden estable de display).
 * `templateKey` es la clave de ruta del endpoint genérico; `templateId` es el
 * id vivo en este office key; `label` es el nombre limpio para UI.
 */
export const EHR_NOTE_TEMPLATES = Object.freeze([
  { templateKey: "follow-up-exam-20-v2",            templateId: "100001824", label: "Follow Up Exam 2.0" },
  { templateKey: "hospital-discharge-follow-up-v2", templateId: "100001823", label: "Hospital Discharge Follow Up" },
  { templateKey: "annual-wellness-20-v2",           templateId: "100001825", label: "Annual Wellness 2.0" },
  { templateKey: "telehealth-20-v2",                templateId: "100001781", label: "Telehealth 2.0" },
  { templateKey: "yearly-physical",                 templateId: "100001826", label: "Yearly Physical" },
  { templateKey: "well-women",                      templateId: "100001828", label: "Well Women" },
  { templateKey: "visit-5-12-years-old",            templateId: "100001827", label: "Visit 5-12 years Old" },
]);

/**
 * Obtiene las notas clínicas de un paciente para UN template (acción
 * `getehrnotes` de AdvancedMD, resuelta por templateKey en el backend).
 *
 * @param {string} templateKey - clave del catálogo (ej. "yearly-physical").
 * @param {string|number} patientId - patientId INTERNO de AMD (no chart number).
 *
 * Ejemplo de llamada:
 * GET /api/advancedmd/ehrnotes/yearly-physical/169040
 * Authorization: Bearer {jwt}
 *
 * Respuesta exitosa (200) — SIEMPRE un array (vacío si el paciente no tiene
 * notas de ese template; eso NO es error):
 * [
 *   {
 *     "noteId": "100289002",
 *     "templateName": "TELEHEALTH_2.0",
 *     "noteDateTime": "2026-06-26T10:38:00",
 *     "userName": "MYRLENE EXANTUS RN",
 *     "signedByUser": "MYRLENE EXANTUS RN",
 *     "reviewedByUser": "",
 *     "confidential": false,
 *     "comments": "",
 *     "fields": { "HPI_Description": "...", "DX_Diagnosis": "E11\nhypertriglyceridemia" }
 *   }
 * ]
 *
 * OJO: `fields` solo trae los controles que existen en ESA instancia de la
 * nota — nunca asumir que todos los campos del template regresan. Los
 * checkbox se LEEN como "Yes"/"No" (aunque se insertan como "1"/"0").
 *
 * Posibles errores:
 * - 400: { "message": "..." } — patientId vacío o inválido.
 * - 404: { "message": "Plantilla 'foo' no existe." } — templateKey fuera del catálogo.
 * - 422/429/502: ProblemDetails (RFC 7807) — AMD rechazó / rate limit / transporte.
 *
 * @returns {Promise<Array>} notas del template (posiblemente vacío)
 */
export async function getEhrNotesByTemplate(templateKey, patientId) {
  try {
    const url = `${EHR_NOTES_BASE_URL}/${encodeURIComponent(templateKey)}/${encodeURIComponent(patientId)}`;
    const res = await axios.get(url, { headers: authHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.error(`Error getEhrNotesByTemplate (${templateKey}):`, err);
    throw err;
  }
}

/**
 * Fan-out sobre los 7 templates activos: trae todas las notas del paciente
 * agrupadas por templateKey. Los templates que fallen no tumban al resto
 * (Promise.allSettled) — devuelven `error` y `notes: []`.
 *
 * @param {string|number} patientId - patientId interno de AMD.
 * @returns {Promise<Object>} mapa por templateKey:
 * {
 *   "yearly-physical": { templateKey, templateId, label, notes: [...], error: null },
 *   "well-women":      { ..., notes: [], error: Error }   // si esa key falló
 * }
 */
export async function getAllEhrNotesByTemplates(patientId) {
  const settled = await Promise.allSettled(
    EHR_NOTE_TEMPLATES.map((t) => getEhrNotesByTemplate(t.templateKey, patientId))
  );

  const out = {};
  EHR_NOTE_TEMPLATES.forEach((t, i) => {
    const r = settled[i];
    out[t.templateKey] = {
      ...t,
      notes: r.status === "fulfilled" ? r.value : [],
      error: r.status === "rejected" ? r.reason : null,
    };
  });
  return out;
}
